import {useRef, useState, useEffect, useCallback} from 'react';
import {convertPointsToList} from './convertPointsToList.js';
import {crop} from 'engine/Crop.js';
import Tone from 'tone';

let counter = 0;
export const useZoom = (buffer, hovered, mouseX, width, waveformCache, sampler) => {
    const [data, setData] = useState({});
    const zoomedBuffer = useRef(undefined);
    const zoomedStartTime = useRef(undefined);
    const zoomScale = useRef(1);

    useEffect(() => {
        zoomOut();
    }, [buffer, sampler]);

    useEffect(() => {
        window.addEventListener('mousewheel', onMouseWheel, {passive: false});

        return () => window.removeEventListener('mousewheel', onMouseWheel);
    }, [
        setData,
        data,
        sampler,
        hovered,
        buffer]);

    const onMouseWheel = useCallback((e) => {
        if (!hovered) {
            return true;
        }

        if (e.ctrlKey) {
            e.preventDefault();
            e.stopPropagation();
            let factor = e.deltaY > 0 ? 0.02 : 0.01;
            let newZoomScale = zoomScale.current - e.deltaY * factor;

            if (newZoomScale <= 1) {
                zoomOut();
                return;
            }

            zoomScale.current = newZoomScale;
            zoomIn(newZoomScale);
        } else {
            let delta = e.deltaX;
            if (Math.abs(delta) > 0) {
                e.preventDefault();
                scrollWaveform(delta);
            }
        }
        return false;
    }, [hovered,
        sampler,
        buffer,
        data, 
        setData
        ]);

    const zoomIn = useCallback((scale) => {
        let startX = mouseX.current;
        scale *= 2;

        if (scale <= 2) {
            zoomOut();
            return;
        }

        let zStart = zoomedStartTime.current || 0;
        let goalDuration = buffer.duration / scale;
        // now we have to fit the start and end to equal goal duration
        // and we want the pointerSeconds to occur at startX position
        let actualSeconds = zStart + convertSpaceToSeconds(startX);
        let goalSeconds = goalDuration * startX / width;
        let start = actualSeconds - goalSeconds;
        let end = start + goalDuration;
            
        if (start < 0) {
            start = 0;
        }
        if (end - start < 0.001) {
            return;
        }

        let sampleRate = Tone.Master.context.sampleRate;
        zoomedStartTime.current = start;
        let cropped = crop(buffer, start * sampleRate, end * sampleRate, Tone.Master.context);
        computeWaveformPoints(cropped, start);
    }, [
        sampler,
        buffer,
        data,
        setData
    ]);

    const zoomOut = useCallback(() => {
        waveformCache.index+=100;
        zoomedStartTime.current = undefined;
        zoomedBuffer.current = undefined;
        zoomScale.current = 1;
        setData({});
    }, [setData]);

    const scrollWaveform = useCallback((delta) => {
        let duration = (zoomedBuffer.current || buffer).duration;

        delta /= (700 * zoomScale.current / buffer.duration);
        let start = zoomedStartTime.current || 0;
        start += delta;
        let end = duration + (zoomedStartTime.current || 0);
        end += delta;

        if (start < 0 || end > buffer.duration) {
            return;
        }

        let sampleRate = Tone.Master.context.sampleRate;
        zoomedStartTime.current = start;
        let cropped = crop(buffer, start * sampleRate, end * sampleRate, Tone.Master.context);
        computeWaveformPoints(cropped, start);
    }, [
        sampler,
        buffer,
        data,
        setData]);

    const computeWaveformPoints = useCallback((cropped, start) => {
        zoomedBuffer.current = cropped;
        if (buffer.duration < 3) {
            waveformCache.getBasicWaveform(0, cropped, false)
                .then((pts) => {
                    if (!pts || zoomScale.current === 1) {
                        return;
                    }
                    pts.sort((a,b) => a.x - b.x);
                    setData({
                        rmsPoints:convertPointsToList(pts, 'rmsY'),
                        waveformPoints: convertPointsToList(pts, 'y'),
                    });
                });
        } else {
            let pointWidth = Math.ceil(8 / Math.pow(zoomScale.current, 2));
            waveformCache.getWaveform(0, cropped, false, pointWidth)
                .then((pts) => {
                    if (!pts || zoomScale.current === 1) {
                        return;
                    }
                    setData({
                        coloredWaveformPoints: pts,
                    });
                });
        }
    }, [sampler, buffer, setData]);

    const convertSpaceToSeconds= useCallback((x) => {
        let buf = zoomedBuffer.current || buffer;
        return buf.duration * x / width;
    }, [buffer, sampler, width]);

    const convertSecondsToSpace = useCallback((x) => {
        let buf = zoomedBuffer.current || buffer;
        return x / buf.duration * width
    }, [buffer, sampler, width]);

    return {
        zoomedBuffer: zoomedBuffer.current,
        zoomedRmsPoints: data.rmsPoints,
        zoomedColoredPoints: data.coloredWaveformPoints,
        zoomedWaveformPoints: data.waveformPoints,
        zoomedStartTime: zoomedStartTime.current};
};
